import { Clock } from "three";

// Simple animation class because THREE's animation system was too complicated and poorly documented
export class Animation {

    static STATE = {
        initial: 0,
        delaying: 1,
        started: 2,
        finished: 3,
    }

    constructor (object, property, endValue, length, delay) {
        this.clock = new Clock();
        this.length = length;

        // If delay is falsy, this sets it to 0, otherwise sets it to delay.
        this.delay = delay || 0;
        this.property = property;
        this.object = object;

        // Error checks
        if (typeof object[property] === 'undefined') {
            console.error('Object' , object , ' does not have property ' , property)
            throw new Error('Object' + object + ' does not have property ' + property);
        }

        // The start value we clone so that we're not accidentally modifying startValue when we modify the property
        this.startValue = this.clone (object[property]);
        this.endValue = endValue;

        // Create a promise to resolve once the animation completes
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });

        this.state = Animation.STATE.initial;
    }

    // Immediately stop an animation and sets its state to finished
    kill() {
        if (this.state == Animation.STATE.finished)
            return;

        this.state = Animation.STATE.finished;

        // Maybe should reject here?
        this.reject();
    }

    setEasing(func) {
        this.easing = func;
        return this;
    }

    // Create and return a new animation with this one's state and properties on a new object
    duplicateTo(obj) {
        const animation = new Animation(obj, this.property, this.endValue, this.length, this.delay);

        // Copy over values
        animation.state = this.state;
        animation.startValue = this.clone(this.startValue);

        animation.clock.autoStart = this.clock.autoStart;
		animation.clock.startTime = this.clock.startTime;
		animation.clock.oldTime = this.clock.oldTime;
		animation.clock.elapsedTime = this.clock.elapsedTime;
		animation.clock.running = this.clock.running;

        animation.easing = this.easing;

        return animation;
    }

    update () {

        if (this.state == Animation.STATE.finished) {
            return;
        }
        else if (this.state == Animation.STATE.initial) {
            // The point of the initial state is to make sure we never start animating on the frame that the animation was created.
            // That is one of the assumptions of promises and we are trying to honor that.
            this.state = Animation.STATE.delaying;
            return;
        } else if (this.state == Animation.STATE.delaying) {

            // Start the delay clock
            if (!this.clock.running)
                this.clock.start();

            // Delay until this.delay seconds have passed (if delay is 0, this immediately goes through)
            if (this.clock.getElapsedTime() < this.delay) {
                return;
            }
            
            // Restart the clock
            this.clock.start();
            this.state = Animation.STATE.started;

        }
        
        const now = this.clock.getElapsedTime();

        // Check if the animation is complete
        if (now >= this.length) {
            this.state = Animation.STATE.finished;
            
            // Finalize the animation
            this.setProperty(this.endValue);

            // Resolve the promise (for code that needs to run when the animation finishes)
            this.resolve();

            return;
        }

        // Convert to unit interval
        var unitNow = now / this.length;

        if (this.easing)
            unitNow = this.easing(unitNow);

        this.lerpProperty(unitNow);
    }

    // Helper to set different types of property
    setProperty (property2) {

        const property = this.object[this.property];

        if (property.isColor || property.isVector3) {

            // Color & Vector3
            property.copy(property2);

        } else {

            // Default - assumed to be number
            this.object[this.property] = property2;
        }
    }

    // Helper to copy different types of property
    clone (property) {

        if (property.isColor || property.isVector3) {

            // Color & Vector3
            return property.clone();

        } else {

            // Default - assumed to be number
            return property;
        }
    }

    // Helper that lerps differently depending on the type of object property
    lerpProperty (t) {

        const property = this.object[this.property];

        if (property.isColor) {

            // Color
            property.lerpColors(this.startValue, this.endValue, t);

        } else if (property.isVector3) {

            // Vector3
            property.lerpVectors(this.startValue, this.endValue, t);

        } else {

            // Default - assumed to be number
            const currentValue = (1 - t) * this.startValue + t * this.endValue;
            this.object[this.property] = currentValue;

        }
    }
}

// A simple class to manage multiple animations
export class AnimationGroup {
    constructor () {

        // Sparse array of animations
        this.animations = [];
        
        // How many animations to let finish before we condense the array
        this.flushAt = 1000;
        this.nFinished = 0;
    }

    add ( animation ) {
        this.animations.push(animation);
        return animation;
    }

    // Get all animations acting on this object
    getAll(...objects) {
        return this.animations.filter(animation => animation && objects.includes(animation.object));
    }

    // Kill all animations acting apon any of these objects
    killAll(...objects) {
        this.getAll(...objects).forEach(animation => animation.kill());
    }

    update() {
        for (var i = 0; i < this.animations.length; i++) {
            if (!this.animations[i])
                continue;

            this.animations[i].update();

            if (this.animations[i].state == Animation.STATE.finished) {
                this.nFinished++;
                this.animations[i] = null;
            }
        }

        // Remove finished animations from the array, condensing it
        if (this.nFinished >= this.flushAt) {

            this.animations = this.animations.filter( animation => animation != null );
            this.nFinished = 0;

        }
    }
}